import React, { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js'
import { CardElement, Elements, ElementsConsumer } from '@stripe/react-stripe-js';
import LoadingLogo from './LoadingLogo'
import SendRequestButton from './SendRequestButton'
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import {
  StripePublishableKey,
  getCsrfTokenFromCookies,
  sanitizeEmailAddress,
  subdomainUrls,
  delay
} from '../../utils'

import {
  REQUEST_UNSTARTED,
  REQUEST_FETCHING,
  REQUEST_SUCCESS,
  REQUEST_ERROR,
} from '../../constants/requestStates'

import {
  LockSvg,
  OvalSpinnerSvg,
} from '../../constants/svgs'

import agent from '../../agent'

import { Haptics, ImpactStyle, NotificationType } from '../../hapticUtils';

// Constants for pricing plans
const PLAN_OPTIONS = {
  QUARTER: {
    id: 'quarter',
    name: '3 Months',
    pricePerSeat: 3999 // $39.99 in cents
  },
  ANNUAL: {
    id: 'annual',
    name: '1 Year',
    pricePerSeat: 9999 // $99.99 in cents
  }
};

// Discount tiers
const DISCOUNT_TIERS = [
  { minSeats: 50, percentage: 10 },
  { minSeats: 100, percentage: 15 },
  { minSeats: 200, percentage: 20 },
  { minSeats: 500, percentage: 25 }
];

const MinSeatQuantity = 10;
const MaxSeatQuantity = 1000;

const NEUROFIT_FONTS = [
  {
    src: 'url(https://neurofit.app/fonts/jost-variable.woff2?v=4)',
    family: 'Futura',
    style: 'normal',
    weight: '420'
  }
]

const SeatNumberStepper = ({ title, minValue, maxValue, value, onUpdateValue }) => {
  // Keep track of what's being typed separately from the actual value
  const [inputValue, setInputValue] = useState(value.toString());
  const [isValid, setIsValid] = useState(true);
  const inputRef = useRef(null);

  // Update internal input value when external value changes
  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  const validateAndCommit = (newValue) => {
    let parsedValue = parseInt(newValue, 10);
    
    if (isNaN(parsedValue)) {
      // Invalid number - reset to current value
      setInputValue(value.toString());
      setIsValid(false);
      return;
    }
    
    // Clamp value to min/max
    if (parsedValue < minValue) {
      parsedValue = minValue;
    } else if (parsedValue > maxValue) {
      parsedValue = maxValue;
    }
    
    // Update both input and parent component
    setInputValue(parsedValue.toString());
    onUpdateValue(parsedValue);
    setIsValid(true);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    
    // Allow any input while typing, even if it would be invalid
    setInputValue(newValue);
    
    // Check if current input is valid (but don't commit yet)
    const parsedValue = parseInt(newValue, 10);
    const currentlyValid = !isNaN(parsedValue) && 
                         parsedValue >= minValue && 
                         parsedValue <= maxValue;
    
    setIsValid(currentlyValid);
    if (currentlyValid) {
      validateAndCommit(parsedValue);
    }
  };

  const handleBlur = () => {
    validateAndCommit(inputValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      validateAndCommit(inputValue);
      inputRef.current.blur();
    }
  };

  return (
    <div style={{width: "100%"}}>
      <h3 style={{
        fontFamily: "Futura", 
        fontSize: "min(5.5vw, 22px)", 
        marginBottom: "10px"
      }}>
        {title}
      </h3>
      <div style={{display: "flex", alignItems: "center", width: "100%"}}>
        <input
          ref={inputRef}
          type="text" // Changed from "number" to prevent native spinners
          inputMode="numeric" // Better numeric keyboard on mobile
          pattern="[0-9]*" // Numeric pattern
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={`application-input application-input-checkout`}
          style={{
            transition: "border-color 0.2s ease, border-width 0.2s ease"
          }}
        />
      </div>
      {!isValid && (
        <div style={{
          color: "#ff6b6b", 
          fontSize: "14px", 
          marginTop: "5px",
          fontFamily: "Futura"
        }}>
          {`Please enter a number between ${minValue} and ${maxValue}.`}
        </div>
      )}
    </div>
  );
};


class CheckoutForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      ready: false,
      isComplete: false,
      isProcessingPayment: false,
      organizationName: "",
      nameOnCard: "",
      workEmail: "",
      seatQuantity: MinSeatQuantity,
      selectedPlan: PLAN_OPTIONS.QUARTER.id,
      paymentStatus: REQUEST_UNSTARTED,
      paymentFailureMessage: ""
    };
  }

  hapticLightOnNative = () => {
    if (this.props.isNative) {
      Haptics.impact({ style: ImpactStyle.Light })
    }
  }

  hapticSuccessOnNative = () => {
    if (this.props.isNative) {
      Haptics.notification({ type: NotificationType.Success })
    }
  }

  getDiscountPercentage = (seatCount) => {
    // Find the highest applicable discount
    const tier = [...DISCOUNT_TIERS]
      .sort((a, b) => b.minSeats - a.minSeats)
      .find(t => seatCount >= t.minSeats);
    
    return tier ? tier.percentage : 0;
  }

  getTotalPriceUsdCents = () => {
    const { seatQuantity, selectedPlan } = this.state;
    
    // Get base price per seat
    const pricePerSeat = selectedPlan === PLAN_OPTIONS.ANNUAL.id
      ? PLAN_OPTIONS.ANNUAL.pricePerSeat
      : PLAN_OPTIONS.QUARTER.pricePerSeat;
    
    // Calculate discount
    const discountPercentage = this.getDiscountPercentage(seatQuantity);
    
    // Apply discount to total
    const totalPriceUsdCents = Math.ceil(pricePerSeat * seatQuantity * (1 - (discountPercentage / 100)));
    
    return { totalPriceUsdCents, discountPercentage };
  }

  extractDomainFromEmail = (email) => {
    if (!email || !email.includes('@')) return '';
    return email.split('@')[1];
  }

  handleSubmit = async (event) => {
    // Block native form submission.
    (!!event) && event.preventDefault();

    const {
      stripe,
      elements,
      onPaymentSuccess,
      onShowErrorMessage,
    } = this.props;

    const {
      organizationName,
      nameOnCard,
      workEmail,
      isProcessingPayment,
      seatQuantity,
      selectedPlan
    } = this.state;

    if (!stripe || !elements || isProcessingPayment) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }

    this.setState({isProcessingPayment: true, paymentStatus: REQUEST_FETCHING});

    try {
      this.hapticLightOnNative()
    } catch (error) {
      console.error(error)
    }

    try {
      // Create payment method
      const {paymentMethod, error} = await stripe
        .createPaymentMethod({
          elements,
          params: {
            billing_details: {
              name: nameOnCard,
              email: workEmail,
            },
          },
        });
      
      if (!!error) {
        throw error;
      }

      // Send to backend - replace with your API endpoint
      const { payment_intent } = await agent.NeuroFitBackend.purchaseOrganizationSeats({
        payment_method_id: paymentMethod.id,
        email_address: workEmail,
        organization_name: organizationName,
        name_on_card: nameOnCard,
        seat_quantity: seatQuantity,
        plan_type: selectedPlan,
      });

      const { client_secret, status, id } = payment_intent;
      
      // Handle 3DS if needed.
      if (status === "requires_action") {
        const { error } = await stripe.confirmCardPayment(client_secret);
        if (error) {
          console.log(error.message);
          onShowErrorMessage("Something went wrong with this payment method. Please try a different card.");
          this.setState({
            isProcessingPayment: false, 
            paymentStatus: REQUEST_ERROR, 
            paymentFailureMessage: `Something went wrong with this card: ${error.message} Please try a different card.`
          });
          return;
        }
      }

      // Payment successful
      this.setState({isProcessingPayment: false, paymentStatus: REQUEST_SUCCESS});
      onPaymentSuccess({
        purchasedSeatQuantity: seatQuantity, 
        planType: selectedPlan,
        checkoutEmailAddress: workEmail
      });
      this.hapticSuccessOnNative()
    } catch (error) {
      this.setState({
        isProcessingPayment: false, 
        paymentStatus: REQUEST_ERROR, 
        paymentFailureMessage: `Something went wrong with this card: ${error.message} Please try a different card.`
      });
    }
  }

  handleUpdateQuantity = (newQuantity) => {
    this.setState({seatQuantity: newQuantity});
  }

  handleUpdateOrganizationName = (organizationName) => {
    this.setState({organizationName})
  }

  handleUpdateNameOnCard = (nameOnCard) => {
    this.setState({nameOnCard});
  }

  handleUpdateWorkEmail = (inputEmailAddress) => {
    const sanitizedEmail = sanitizeEmailAddress(inputEmailAddress);
    this.setState({
      workEmail: sanitizedEmail
    });
  }

  handlePlanSelection = (planId) => {
    this.setState({selectedPlan: planId});
  }

  handleOnReady = () => {
    this.setState({ready: true});
    this.props.onReady();
  }

  handleUpdatedCompletionStatus = isComplete => {
    this.setState({isComplete});
  }

  render() {
    const {
      stripe,
      showStripeLoadError,
      showStripeSlowLoadingError,
    } = this.props;

    const {
      ready,
      isComplete,
      isProcessingPayment,
      paymentStatus,
      paymentFailureMessage,
      organizationName,
      nameOnCard,
      workEmail,
      seatQuantity,
      selectedPlan
    } = this.state;

    const readyForCheckout = isComplete && organizationName && nameOnCard && workEmail;
    const fullCheckoutFormLoaded = ready;
    const showLoadingIndicator = !fullCheckoutFormLoaded && !showStripeLoadError && !showStripeSlowLoadingError;
    
    const { totalPriceUsdCents, discountPercentage } = this.getTotalPriceUsdCents();
    const formattedTotalPrice = (totalPriceUsdCents / 100).toFixed(2);
    
    const selectedPlanName = selectedPlan === PLAN_OPTIONS.ANNUAL.id 
      ? PLAN_OPTIONS.ANNUAL.name 
      : PLAN_OPTIONS.QUARTER.name;

    return (
      <div style={{display: "flex", width: "100%", justifyContent: "center"}}>
        <div style={{maxWidth: 600, color: "#000"}}>
          <SwitchTransition mode="out-in">
            <CSSTransition
              timeout={400}
              classNames={"fade-in"}
              unmountOnExit
              addEndListener={(node, done) => {
                node.addEventListener("transitionend", done, false);
              }}
              key={`${showLoadingIndicator}-${showStripeLoadError}-${showStripeSlowLoadingError}`}
            >
              <div>
                {showLoadingIndicator && (
                  <div style={{display: "flex", justifyContent: "center", marginTop: "5vh"}}>
                    <LoadingLogo
                      show={true}
                      hideLogo
                      responsiveSize={"min(7vw, 35px)"}
                      marginTop={"0px"}
                      color={"black"}
                      fontFamily={"Futura"}
                      text={"LOADING CHECKOUT..."}
                      showProgressionBar={false}
                    />
                  </div>
                )}
                {showStripeSlowLoadingError && (!showStripeLoadError) && (!fullCheckoutFormLoaded) &&  (
                  <div
                    style={{fontFamily: "Futura", textAlign: "center", marginTop: "5vh", fontSize: "min(4vh, 30px)", color: "#000000"}}
                  >
                    <span>
                      {"Checkout is slow to load due to network issues."}
                    </span>
                    <div style={{display: "flex", justifyContent: "center", marginTop: "2vh"}}>
                      <button
                        style={{display: "flex", textTransform: "uppercase", cursor: "pointer", alignItems: "top", fontSize: "min(6vw, 26px)", fontFamily: "Futura", justifyContent: "center", width: "100%", maxWidth: 400, textAlign: "center", fontFamily: "Futura", fontSize: "min(6vw, 25px)", lineHeight: "min(6vw, 25px)", marginTop: 10, color: "#FFFFFF", backgroundColor: "#000000", padding: "18px 15px", border: "none"}}
                        onClick={() => {
                          window.location.reload(true);
                        }}
                      >
                        {"Reload Checkout"}
                      </button>
                    </div>
                  </div>
                )}
                {showStripeLoadError && (!fullCheckoutFormLoaded) && (!showStripeSlowLoadingError) && (
                  <div
                    style={{fontFamily: "Futura", textAlign: "center", marginTop: "5vh", fontSize: "min(4vh, 30px)", color: "#000000"}}
                  >
                    <span>
                      {"Checkout failed to load due to network issues."}
                    </span>
                    <div style={{display: "flex", justifyContent: "center", marginTop: "2vh"}}>
                      <button
                        style={{display: "flex", textTransform: "uppercase", cursor: "pointer", alignItems: "top", fontSize: "min(6vw, 26px)", fontFamily: "Futura", justifyContent: "center", width: "100%", maxWidth: 400, textAlign: "center", fontFamily: "Futura", fontSize: "min(6vw, 25px)", lineHeight: "min(6vw, 25px)", marginTop: 10, color: "#FFFFFF", backgroundColor: "#000000", padding: "18px 15px", border: "none"}}
                        onClick={() => {
                          window.location.reload(true);
                        }}
                      >
                        {"Reload Checkout"}
                      </button>
                    </div>
                  </div>
                )}
                <div style={{opacity: (fullCheckoutFormLoaded) ? 1.0 : 0.0, backgroundColor: "transparent", transition: "opacity 300ms linear"}}>
                  <div>                    
                    {/* Plan Selection */}
                    <div style={{marginBottom: "20px"}}>
                      <h3 style={{fontFamily: "Futura", fontSize: "min(5.5vw, 22px)", marginBottom: "10px"}}>
                        Select Plan Duration
                      </h3>
                      <div style={{display: "flex", justifyContent: "space-between"}}>
                        {Object.values(PLAN_OPTIONS).map(plan => (
                          <button
                            key={plan.id}
                            style={{
                              width: "48%",
                              padding: "15px",
                              border: `2px solid ${selectedPlan === plan.id ? "#000" : "#ddd"}`,
                              backgroundColor: selectedPlan === plan.id ? "#fff" : "#f7f7f7",
                              fontFamily: "Futura",
                              fontSize: "min(4.5vw, 20px)",
                              color: selectedPlan === plan.id ? "#000000" : "#555555",
                              cursor: "pointer"
                            }}
                            onClick={() => this.handlePlanSelection(plan.id)}
                          >
                            <div style={{fontFamily: "Futura Medium"}}>{plan.name}</div>
                            <div style={{fontSize: "min(4vw, 18px)"}}>${(plan.pricePerSeat / 100).toFixed(2)} per seat</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Seat Quantity Selector */}
                    <div style={{backgroundColor: "#EDEEF2", marginTop: "5vh", width: "100%"}}>
                      <div style={{width: "100%"}}>
                        <SeatNumberStepper
                          title={`Enter Team Size (${MinSeatQuantity} - ${MaxSeatQuantity.toLocaleString()} Seats)`}
                          minValue={MinSeatQuantity}
                          maxValue={MaxSeatQuantity}
                          value={seatQuantity}
                          onUpdateValue={(quantity) => this.handleUpdateQuantity(quantity)}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div style={{backgroundColor: "#EDEEF2", marginTop: "5vh", width: "calc(100% - 30px)", padding: "15px", backgroundColor: "#f7f7f7", boxShadow: "0px 0px 0.5px 1.5px #DDDDDD"}}>
                    <div style={{fontSize: "min(5.5vw, 22px)", paddingInlineEnd: "0.5em", textAlign: "start", fontFamily: "Futura", color: "#000000"}}>
                      {`Your Team App Access Details`}
                    </div>
                    <div style={{fontSize: "min(5vw, 20px)", paddingInlineEnd: "0.5em", marginTop: "1vh", textAlign: "start", fontFamily: "Futura", color: "#777"}}>
                      {`$${formattedTotalPrice} USD`}
                    </div>
                    {discountPercentage > 0 && (
                      <div style={{fontSize: "min(4.5vw, 18px)", color: "#000", marginTop: "1vh", display: "inline-block", textTransform: "uppercase", backgroundColor: "#000000", color: "#FFFFFF", padding: "0.2em 0.5em", textAlign: "start", fontFamily: "Futura", transition: "all 200ms linear", transitionProperty: "background-color, color"}}>
                        {`Saving ${discountPercentage}%`}
                      </div>
                    )}
                    <div style={{marginInlineEnd: "0.2em", color: "#000000", marginTop: "1vh", fontSize: "min(4.5vw, 18px)", fontFamily: "Futura"}}>
                      {`After checkout, you'll receive ${selectedPlanName.toLowerCase()} of app access for ${seatQuantity} team members.`}
                    </div>
                  </div>
                  
                  <form onSubmit={this.handleSubmit}>
                    <div style={{display: "flex", alignItems: "end", justifyContent: "left", marginTop: "4vh", marginBottom: "min(24px, max(3vw, 16px))"}}>
                      <LockSvg
                        height={"min(4vw, 18px)"}
                        width={"min(4vw, 18px)"}
                        color={"#000000"}
                      />
                      <div style={{marginInlineEnd: "0.2em", color: "#000000", marginLeft: "0.2em", fontSize: "min(4vw, 18px)", lineHeight: "min(3.5vw, 16px)", fontFamily: "Futura"}}>
                        {"All transactions are secure and encrypted."}
                      </div>
                    </div>
                    

                    <div style={{margin: "10px 0px"}}>
                      <input
                        type="text"
                        id="name-on-card"
                        className={"application-input application-input-checkout"}
                        placeholder={"Organization name"}
                        autoCapitalize="words"
                        autoCorrect="false"
                        autoComplete="cc-name"
                        spellCheck={false}
                        value={organizationName}
                        disabled={isProcessingPayment}
                        onChange={(e) => this.handleUpdateOrganizationName(e.target.value)}
                      />
                    </div>
                    
                    <div style={{margin: "10px 0px"}}>
                      <input
                        id="work-email-address"
                        type="email"
                        className={"application-input application-input-checkout"}
                        placeholder={"Contact email address"}
                        autoCapitalize="off"
                        autoCorrect="false"
                        autoComplete="email"
                        spellCheck={false}
                        value={workEmail}
                        disabled={isProcessingPayment}
                        onChange={(e) => this.handleUpdateWorkEmail(e.target.value)}
                      />
                    </div>

                    <div style={{margin: "10px 0px"}}>
                      <input
                        type="text"
                        id="name-on-card"
                        className={"application-input application-input-checkout"}
                        placeholder={"Name on card"}
                        autoCapitalize="words"
                        autoCorrect="false"
                        autoComplete="cc-name"
                        spellCheck={false}
                        value={nameOnCard}
                        disabled={isProcessingPayment}
                        onChange={(e) => this.handleUpdateNameOnCard(e.target.value)}
                      />
                    </div>
                    
                    <div className={"application-input"} style={{height: 22, padding: "12px 8px", marginBottom: "10px", textRendering: "geometricPrecision"}}>
                      <CardElement
                        onReady={() => this.handleOnReady()}
                        onChange={e => {
                          this.handleUpdatedCompletionStatus(e.complete);
                        }}
                        options={{
                          style: {
                            base: {
                              fontSize: "18px",
                              fontFamily: 'Futura, sans-serif',
                              fontSmoothing: "antialiased",
                              color: '#000000',
                              '::placeholder': {
                                color: '#BABDC6',
                              },
                              backgroundColor: "#FFFFFF"
                            },
                            invalid: {
                              color: '#9e2146',
                            },
                          }
                        }}
                      />
                    </div>
                    <SendRequestButton
                      defaultContent={`Purchase ${seatQuantity} Seat${seatQuantity === 1 ? "" : "s"}`}
                      fontSize={"18px"}
                      fontWeight={"normal"}
                      padding={"11px"}
                      backgroundColor={"#000000"}
                      textTransform={"none"}
                      color={(paymentStatus === REQUEST_SUCCESS) ? "rgba(255, 255, 255, 0.4)" : "#ffffff"}
                      loadingText={"Processing..."}
                      successText={"Purchase Successful"}
                      requestState={paymentStatus}
                      cancelOverflow
                      isDisabled={!stripe || !readyForCheckout || (paymentStatus === REQUEST_SUCCESS)}
                      isSubmit
                    />
                  </form>
                  
                  <SwitchTransition mode="out-in">
                    <CSSTransition
                      timeout={500}
                      classNames={"fade-in"}
                      unmountOnExit
                      addEndListener={(node, done) => {
                        node.addEventListener("transitionend", done, false);
                      }}
                      key={paymentStatus}
                    >
                      <div>
                        {(paymentStatus === REQUEST_UNSTARTED) && (
                          <div style={{fontSize: "min(4vw, 18px)", color: "#999999", fontFamily: "Futura", marginTop: "2vh"}}>
                            <span style={{marginInlineEnd: "0.2em"}}>{`By purchasing team app seats above, you agree to our `}</span>
                            <a style={{color: "#999999"}} href={`${subdomainUrls.baseLegalUrl}/#terms`} target="_blank" rel="noopener noreferrer nofollow">{"Platform Terms"}</a>
                            <span style={{marginInlineEnd: "0.2em"}}>{" & "}</span>
                            <a style={{color: "#999999"}} href={`${subdomainUrls.baseLegalUrl}/#terms`} target="_blank" rel="noopener noreferrer nofollow">{"Privacy Policy"}</a>
                            <span>{"."}</span>
                          </div>
                        )}
                        {(paymentStatus === REQUEST_ERROR) && (
                          <div style={{
                            marginTop: "15px",
                            padding: "10px 15px",
                            backgroundColor: "#ffeeee",
                            border: "1px solid #ff6b6b",
                            color: "#d63031",
                            fontSize: "14px",
                            fontFamily: "Futura"
                          }}>
                            {paymentFailureMessage}
                          </div>
                        )}
                        {(paymentStatus === REQUEST_FETCHING) && (
                          <div style={{
                            marginTop: "15px",
                            padding: "10px 15px",
                            backgroundColor: "#f7f7f7",
                            border: "1px solid #ddd",
                            color: "#666",
                            fontSize: "14px",
                            fontFamily: "Futura"
                          }}>
                            {"Processing payment securely through Stripe..."}
                          </div>
                        )}
                        {(paymentStatus === REQUEST_SUCCESS) && (
                          <div style={{
                            marginTop: "15px",
                            padding: "10px 15px",
                            backgroundColor: "#e6ffee",
                            border: "1px solid #00b894",
                            color: "#00b894",
                            fontSize: "14px",
                            fontFamily: "Futura"
                          }}>
                            {"Payment successful! You'll receive a confirmation email shortly."}
                          </div>
                        )}
                      </div>
                    </CSSTransition>
                  </SwitchTransition>
                </div>
              </div>
            </CSSTransition>
          </SwitchTransition>
        </div>
      </div>
    );
  }
}

class TeamAppAccessStripeCheckoutForm extends React.Component {
  constructor(props) {
    super(props);
    // Make sure to call `loadStripe` outside of a component's render to avoid
    // recreating the `Stripe` object on every render.

    this.state = {
      StripePromise: null,
      showStripeLoadError: false,
      showStripeSlowLoadingError: false,
      ready: false
    };

    this.handleStripeLoadError();
  }

  async handleStripeLoadError() {
    try {
      this.triggerDelayedStripeLoadErrorMessage();
      const StripePromise = await loadStripe(StripePublishableKey);
      this.setState({StripePromise});
    } catch (e) {
      console.log("Failed to load Stripe");
      this.setState({showStripeLoadError: true});
    }
  }

  async triggerDelayedStripeLoadErrorMessage() {
    await delay(10000);
    const {StripePromise, showStripeLoadError, ready} = this.state;
    if (!showStripeLoadError && !ready) {
      this.setState({showStripeSlowLoadingError: true});
    }
  }

  render() {
    const {StripePromise, showStripeLoadError, showStripeSlowLoadingError} = this.state;
    const {onPaymentSuccess, onShowErrorMessage} = this.props;
    
    return (
      <Elements options={{fonts: NEUROFIT_FONTS}} stripe={StripePromise}>
        <ElementsConsumer>
          {({elements, stripe}) => (
            <CheckoutForm
              elements={elements}
              stripe={stripe}
              showStripeLoadError={showStripeLoadError}
              showStripeSlowLoadingError={showStripeSlowLoadingError}
              onPaymentSuccess={onPaymentSuccess}
              onShowErrorMessage={onShowErrorMessage}
              onReady={() => this.setState({ready: true})}
            />
          )}
        </ElementsConsumer>
      </Elements>
    );
  }
}

export default TeamAppAccessStripeCheckoutForm;